import { ActionBar } from "@/components/ActionBar/ActionBar";
import { NoCreditsBanner } from "@/components/Banner/NoCredits";
import { Button } from "@/components/Button/Button";
import { DownloadDesignsButton } from "@/components/Button/Cases/DownloadDesignsButton";
import { ProcessUnpaidUploadButton } from "@/components/Button/Cases/ProcessUnpaidUploadButton";
import { RejectPillButton } from "@/components/Button/Cases/RejectPillButton";
import { RenamePillButton } from "@/components/Button/Cases/RenamePillButton";
import { CaseDetails } from "@/components/CaseDetails/CaseDetails";
import { CasesList } from "@/components/CasesList/CasesList";
import { Dashboard, DashboardLink } from "@/components/Dashboard/Dashboard";
import { AddSvg, CancelledSvg, DownloadSvg, ToAcceptSvg, ToReviewSvg, WarningSvg } from "@/components/Icon/Icon";
import { ReduxPerPage } from "@/components/PerPageSelector/PerPageSelector";
import { RightDrawer } from "@/components/RightDrawer/RightDrawer";
import { SearchField } from "@/components/SearchField/SearchField";
import { TabsTablePageHeader } from "@/components/Tabs/Tabs";
import {
  ADDITIONAL_FILTERS,
  CASE_TABLE_STATE_NAME,
  DefaultTab,
  designTableConstants,
  STATUS_FILTERS,
  TabPath
} from "@/constants/designTableConstants";
import { useOrgCredits } from "@/hooks/credits";
import { trackCreateCaseDialogOpened } from "@/analytics/tracking";
import { UPLOAD_SCANS_MODAL } from "@/pages/Home/DesignSection/AssembledTabsSection/UploadScansModal/UploadScansModal";
import { useGetCasesApiV1CasesGetQuery } from "@/store/clientApi";
import { selectDetailsItem, selectDrawerOpen, setDrawerOpen } from "@/store/slices/detailsDrawerSlice";
import { setModalOpen } from "@/store/slices/modalSlice";
import { getTab } from "@/store/slices/tabSlice";
import {
  invalidate,
  selectIsChecked,
  selectNumChecked,
  selectQueryArgs,
  selectQueryPerPage,
  selectQuerySearchValue,
  setPerPage,
  setRtkArgs,
  setSearchValue
} from "@/store/slices/tableSlice";
import { cacheCaseThumbnails } from "@/store/slices/thumbnailsCacheSlice";
import { RootState, store } from "@/store/store";
import { Box, Theme, useMediaQuery, useTheme } from "@mui/material";
import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

const invalidateTable = () => store.dispatch(invalidate(CASE_TABLE_STATE_NAME));

const CaseDrawer = ({ height }: { height: number }) => {
  const { id: selectedTab } = useParams();
  const location = useLocation();
  const basePath = location.pathname.split("/")[1];

  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down("md"));
  const numSelected = useSelector((state: RootState) => selectDetailsItem(state, basePath, selectedTab)?.selectedIds?.length);

  useEffect(() => {
    const setOpen = (value: boolean) => store.dispatch(setDrawerOpen({ value: value }));
    if (numSelected !== undefined && numSelected >= 1) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [numSelected]);

  return (
    <RightDrawer
      stateName={CASE_DETAILS_STATE}
      width={mdDown ? "100%" : "30vw"}
      shift
      sx={{
        [theme.breakpoints.up("md")]: {
          maxWidth: "460px"
        },
        height: `${height}px`,
        flexGrow: "1",
        flexShrink: "0"
      }}
      padding="0px"
    >
      <CaseDetails />
    </RightDrawer>
  );
};

const CaseActionBar = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation("cases");
  const isSelected = useSelector((state: RootState) => selectIsChecked(state, CASE_TABLE_STATE_NAME));
  const searchValue = useSelector((state: RootState) => selectQuerySearchValue(state, CASE_TABLE_STATE_NAME));

  return (
    <Box
      component="div"
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        alignContent: "center",
        width: "100%",
        gap: "6px"
      }}
    >
      {isSelected ? (
        <ActionBar
          selector={(state: RootState) => selectNumChecked(state, CASE_TABLE_STATE_NAME)}
          handleCloseActionBar={() => {
            invalidateTable();
          }}
        >
          <RejectPillButton />
          <RenamePillButton />
          <DownloadDesignsButton />
          <ProcessUnpaidUploadButton />
        </ActionBar>
      ) : (
        <>
          <SearchField
            placeholder={t("label.search")}
            value={searchValue}
            onChange={(searchInput) => {
              store.dispatch(
                setSearchValue({
                  name: CASE_TABLE_STATE_NAME,
                  value: searchInput
                })
              );
            }}
          />
          <Box
            component="div"
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              width: "100%",
              gap: "6px"
            }}
          >
            {children}
          </Box>
        </>
      )}
    </Box>
  );
};

const CaseData = () => {
  const tab = useSelector((state: RootState) => getTab(state, TabPath)) ?? DefaultTab;
  const queryArgs = useSelector((state: RootState) => selectQueryArgs(state, CASE_TABLE_STATE_NAME));

  const rtkArgs = useMemo(() => {
    const statusFilter = tab ? STATUS_FILTERS[tab] : [];
    const additionalFilters = tab ? ADDITIONAL_FILTERS[tab] : undefined;

    const statusQuery = statusFilter?.length > 0 ? `(${statusFilter.map((status) => `status:${status}`).join(" OR ")})` : undefined;
    const tabQuery =
      statusQuery !== undefined || additionalFilters !== undefined
        ? [statusQuery, additionalFilters].filter((filter) => filter !== undefined).join(" OR ")
        : undefined;

    const defaultSortField = "created_at";
    const defaultSortOrder = "desc";
    const sortField = queryArgs.sortField === "" ? defaultSortField : queryArgs.sortField;
    const sortOrder = queryArgs.sortField === "" ? defaultSortOrder : queryArgs.sortOrder;

    return {
      page: queryArgs.page !== 0 ? queryArgs.page - 1 : 0,
      perPage: queryArgs.perPage,
      sorting: `${sortField}:${sortOrder === "asc" ? "1" : "-1"}`,
      query: queryArgs.searchValue !== "" ? `(name:"*${queryArgs.searchValue}*") ` + (tabQuery ? `AND ${tabQuery}` : "") : tabQuery
    };
  }, [queryArgs, tab]);

  useEffect(() => {
    store.dispatch(setRtkArgs({ name: CASE_TABLE_STATE_NAME, value: rtkArgs }));
  }, [rtkArgs]);

  const { data } = useGetCasesApiV1CasesGetQuery(rtkArgs, {
    pollingInterval: designTableConstants.POLLING_INTERVAL,
    skipPollingIfUnfocused: true
  });

  useEffect(() => {
    store.dispatch(cacheCaseThumbnails(data));
  }, [data]);

  return <>{null}</>;
};

const CreateCaseButton = () => {
  const { t } = useTranslation();
  const { id: selectedTab } = useParams();
  const { isOutOfCredits } = useOrgCredits({});

  const handleOnClickCreate = () => {
    trackCreateCaseDialogOpened();
    store.dispatch(setModalOpen({ name: UPLOAD_SCANS_MODAL, value: true }));
  };

  if (selectedTab !== "all" && selectedTab !== undefined) {
    return <></>;
  }

  if (isOutOfCredits) {
    return <></>;
  }

  return (
    <Button
      variant="contained"
      color="secondary"
      sx={{
        borderRadius: "100px",
        height: "32px",
        gap: "8px"
      }}
      onClick={handleOnClickCreate}
    >
      <AddSvg color="inherit" />
      {t("common:links.create")}
    </Button>
  );
};

const Header = () => {
  const { t } = useTranslation("cases");
  const theme = useTheme();

  return (
    <TabsTablePageHeader
      tabPath={TabPath}
      table={CASE_TABLE_STATE_NAME}
      onEnterTab={() => {
        invalidateTable();
        store.dispatch(
          setSearchValue({
            name: CASE_TABLE_STATE_NAME,
            value: ""
          })
        );
      }}
      title={t("title.designs")}
      defaultTabValue={DefaultTab}
      buttons={<CreateCaseButton />}
      tabs={[
        {
          label: t("filters.all"),
          value: "all",
          icon: <ToReviewSvg color="inherit" />
        },
        {
          label: t("filters.issues"),
          value: "issues",
          icon: <WarningSvg color={theme.palette.warning.main} />
        },
        {
          label: t("filters.designed"),
          value: "designed",
          icon: <ToAcceptSvg color="inherit" />
        },
        {
          label: t("filters.downloaded"),
          value: "downloaded",
          icon: <DownloadSvg color="inherit" />
        },
        {
          label: t("filters.rejected"),
          value: "rejected",
          icon: <CancelledSvg color="inherit" />
        }
      ]}
    />
  );
};

interface CasesDashboardProps {
  primaryLinks: DashboardLink[];
  footerLinks: DashboardLink[];
}

const CasesDashboard: FC<CasesDashboardProps> = (props) => {
  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down("md"));
  const [height, setHeight] = useState<number>(0);
  const detailsOpen = useSelector((state: RootState) => selectDrawerOpen(state));

  return (
    <Box sx={{ height: "100vh" }}>
      <CaseData />
      <Dashboard primaryLinks={props.primaryLinks} footerLinks={props.footerLinks}>
        <Box component="div" sx={{ height: "100%", position: "relative" }}>
          <Box
            component="div"
            sx={{
              display: "flex",
              alignContent: "stretch",
              justifyContent: "space-evenly",
              gap: "1em",
              width: "100%",
              height: "100%"
            }}
            ref={(ref: HTMLDivElement) => {
              if (ref) {
                setHeight(ref.clientHeight ?? 0);
                window.addEventListener("resize", () => setHeight(ref.clientHeight ?? 0));
              }
            }}
          >
            <Box
              component="div"
              sx={{
                display: mdDown ? (detailsOpen ? "none" : "flex") : "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
                bgcolor: (theme: Theme) => theme.surface.low,
                padding: "1em",
                borderRadius: "1em",
                overflow: "auto"
              }}
            >
              <Header />
              <NoCreditsBanner />

              {/* Search bar */}
              <CaseActionBar>
                <ReduxPerPage
                  dispatch={(state) =>
                    store.dispatch(
                      setPerPage({
                        name: CASE_TABLE_STATE_NAME,
                        value: state
                      })
                    )
                  }
                  selector={(state) => selectQueryPerPage(state, CASE_TABLE_STATE_NAME)}
                />
              </CaseActionBar>

              {/* Table rows */}
              <Box
                component="div"
                sx={{
                  maxHeight: "100%",
                  height: "100%",
                  minHeight: "72px",
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  bgcolor: (theme: Theme) => theme.surface.low
                }}
              >
                <CasesList />
              </Box>
            </Box>
            <CaseDrawer height={height} />
          </Box>
        </Box>
      </Dashboard>
    </Box>
  );
};

export const CASE_DETAILS_STATE = "caseDetailsState";
export { CasesDashboard };
